import { RoadmapDetailData } from '../types/roadmap';
import { ROADMAP_WORKFLOW } from '../data/roadmapWorkflow';

export async function exportRoadmapToPDF(data: RoadmapDetailData): Promise<void> {
  const { project, validations, progress, phaseProgresses } = data;

  const content = generatePDFContent(data);

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Veuillez autoriser les pop-ups pour exporter le PDF');
    return;
  }

  printWindow.document.write(content);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.print();
  };
}

function generatePDFContent(data: RoadmapDetailData): string {
  const { project, validations, progress, phaseProgresses } = data;
  const today = new Date().toLocaleDateString('fr-FR');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Feuille de route - ${project.name}</title>
  <style>
    @page {
      size: A4;
      margin: 2cm;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      line-height: 1.6;
      color: #1f2937;
      background: white;
    }

    .header {
      border-bottom: 3px solid #2563eb;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }

    .logo-area {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
    }

    .logo-img {
      max-width: 300px;
      height: auto;
    }

    .date {
      color: #6b7280;
      font-size: 14px;
    }

    h1 {
      font-size: 28px;
      color: #111827;
      margin-bottom: 10px;
    }

    .project-info {
      background: #f3f4f6;
      padding: 15px;
      border-radius: 8px;
      margin-bottom: 20px;
    }

    .info-row {
      display: flex;
      margin-bottom: 8px;
      font-size: 14px;
    }

    .info-label {
      font-weight: 600;
      width: 150px;
      color: #374151;
    }

    .info-value {
      color: #6b7280;
    }

    .progress-section {
      background: #eff6ff;
      border: 2px solid #2563eb;
      border-radius: 8px;
      padding: 20px;
      margin: 30px 0;
      text-align: center;
    }

    .progress-title {
      font-size: 18px;
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 15px;
    }

    .progress-percentage {
      font-size: 48px;
      font-weight: bold;
      color: #2563eb;
      margin-bottom: 10px;
    }

    .progress-bar {
      width: 100%;
      height: 20px;
      background: #dbeafe;
      border-radius: 10px;
      overflow: hidden;
      margin-top: 15px;
    }

    .progress-fill {
      height: 100%;
      background: #2563eb;
    }

    .phase {
      page-break-inside: avoid;
      margin-bottom: 40px;
    }

    .phase-header {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      margin-bottom: 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .phase-header.green {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }

    .phase-header.purple {
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    }

    .phase-header.amber {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    }

    .phase-header.emerald {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }

    .phase-title {
      font-size: 20px;
      font-weight: 600;
    }

    .phase-progress {
      font-size: 16px;
      font-weight: 500;
    }

    .step {
      padding: 15px;
      margin-bottom: 10px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      page-break-inside: avoid;
    }

    .step.validated {
      background: #f0fdf4;
      border-color: #86efac;
    }

    .step-header {
      display: flex;
      align-items: start;
      margin-bottom: 8px;
    }

    .step-checkbox {
      width: 20px;
      height: 20px;
      border: 2px solid #d1d5db;
      border-radius: 4px;
      margin-right: 12px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .step-checkbox.checked {
      background: #10b981;
      border-color: #10b981;
    }

    .step-checkbox.checked::after {
      content: '✓';
      color: white;
      font-weight: bold;
      font-size: 14px;
    }

    .step-title {
      font-weight: 600;
      font-size: 16px;
      color: #111827;
    }

    .step-description {
      color: #6b7280;
      font-size: 14px;
      margin-bottom: 8px;
      padding-left: 32px;
    }

    .step-note {
      background: #fef3c7;
      padding: 10px;
      border-left: 3px solid #f59e0b;
      margin-top: 10px;
      font-size: 14px;
      color: #78350f;
    }

    .step-meta {
      color: #9ca3af;
      font-size: 12px;
      margin-top: 8px;
      padding-left: 32px;
    }

    .validation-badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
      margin-left: 8px;
    }

    .badge-auto {
      background: #dbeafe;
      color: #1e40af;
    }

    .badge-manual {
      background: #e9d5ff;
      color: #6b21a8;
    }

    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 2px solid #e5e7eb;
      text-align: center;
      color: #6b7280;
      font-size: 12px;
    }

    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo-area">
      <img src="/cresslogo.png" alt="CRESS Guadeloupe" class="logo-img" />
      <div class="date">Édité le ${today}</div>
    </div>
    <h1>Feuille de route d'accompagnement ESS</h1>
  </div>

  <div class="project-info">
    <div class="info-row">
      <span class="info-label">Porteur de projet :</span>
      <span class="info-value">${project.owner?.full_name || 'Non renseigné'}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Nom du projet :</span>
      <span class="info-value">${project.name}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Statut actuel :</span>
      <span class="info-value">${project.status.replace(/_/g, ' ')}</span>
    </div>
    <div class="info-row">
      <span class="info-label">Domaine :</span>
      <span class="info-value">${project.domain}</span>
    </div>
    ${
      project.owner?.email
        ? `<div class="info-row">
      <span class="info-label">Email :</span>
      <span class="info-value">${project.owner.email}</span>
    </div>`
        : ''
    }
    ${
      project.owner?.phone
        ? `<div class="info-row">
      <span class="info-label">Téléphone :</span>
      <span class="info-value">${project.owner.phone}</span>
    </div>`
        : ''
    }
  </div>

  <div class="progress-section">
    <div class="progress-title">Progression Globale de l'Accompagnement</div>
    <div class="progress-percentage">${progress?.progress_percentage.toFixed(0)}%</div>
    <div>${progress?.completed_steps} étapes complétées sur ${progress?.total_steps}</div>
    <div class="progress-bar">
      <div class="progress-fill" style="width: ${progress?.progress_percentage || 0}%"></div>
    </div>
  </div>

  ${ROADMAP_WORKFLOW.map((phase) => {
    const phaseProgress = phaseProgresses.find((p) => p.phase_key === phase.key);
    return `
    <div class="phase">
      <div class="phase-header ${phase.color}">
        <div class="phase-title">${phase.label}</div>
        <div class="phase-progress">
          ${phaseProgress?.completed_steps || 0}/${phaseProgress?.total_steps || 3}
        </div>
      </div>

      ${phase.steps
        .map((step) => {
          const validation = validations.find(
            (v) => v.phase_key === phase.key && v.step_key === step.key
          );
          const isValidated = validation?.is_manually_validated || validation?.is_auto_validated;

          return `
        <div class="step ${isValidated ? 'validated' : ''}">
          <div class="step-header">
            <div class="step-checkbox ${isValidated ? 'checked' : ''}"></div>
            <div style="flex: 1;">
              <div class="step-title">
                ${step.label}
                ${
                  validation?.is_auto_validated
                    ? '<span class="validation-badge badge-auto">Auto</span>'
                    : ''
                }
                ${
                  validation?.is_manually_validated
                    ? '<span class="validation-badge badge-manual">Manuel</span>'
                    : ''
                }
              </div>
            </div>
          </div>
          <div class="step-description">${step.description}</div>
          ${
            validation?.note
              ? `<div class="step-note"><strong>Note :</strong> ${validation.note}</div>`
              : ''
          }
          ${
            validation?.validation_date
              ? `<div class="step-meta">Validée le ${new Date(
                  validation.validation_date
                ).toLocaleDateString('fr-FR')}</div>`
              : ''
          }
        </div>
      `;
        })
        .join('')}
    </div>
    `;
  }).join('')}

  <div class="footer">
    <p><strong>CRESS Guadeloupe</strong></p>
    <p>Chambre Régionale de l'Économie Sociale et Solidaire</p>
    <p>Document généré automatiquement le ${today}</p>
  </div>
</body>
</html>
  `;

  return html;
}
